import { useState, useCallback } from "react";
import { useSettings } from "./useSettings";
import { useTextToSpeech } from "./useTextToSpeech";
import { useTranslation } from "./useTranslation";
import { usePiP } from "./usePiP";
import { useMeetings, type TranscriptItem, type Meeting } from "./useMeetings";
import { useRecording, type RecordingStatus } from "./useRecording";
import { useRecordingTimer } from "./useRecordingTimer";

export type AppMode = "traductor" | "minuta";
export type { RecordingStatus, TranscriptItem, Meeting };

export interface UseTranslatorReturn {
  appMode: AppMode;
  recording: boolean;
  recordingDuration: string;
  voiceEnabled: boolean;
  pipActive: boolean;
  status: RecordingStatus;
  statusText: string;
  srcLang: string;
  tgtLang: string;
  meetingTranscript: TranscriptItem[];
  currentMeetingId: string | null;
  subtitleOrig: string;
  subtitleTran: string;
  settings: {
    translatorServiceUrl: string;
    summaryServiceUrl: string;
    userServiceUrl: string;
  };

  setAppMode: (mode: AppMode) => void;
  setVoiceEnabled: (v: boolean) => void;
  setSrcLang: (l: string) => void;
  setTgtLang: (l: string) => void;

  toggleRecording: (onStartedCb?: () => void) => void;
  togglePip: () => void;
  saveSettings: (newSettings: {
    translatorServiceUrl: string;
    summaryServiceUrl: string;
    userServiceUrl: string;
  }) => void;
  selectHistoryItem: (item: TranscriptItem) => void;
  speakTranslation: (text: string, langCode: string, force?: boolean) => void;
  getStatusColorClass: () => string;
  getLanguageLabel: (code: string) => string;
  loadPastMeeting: (
    meetingId: string,
    transcripts: TranscriptItem[],
    sourceLang: string,
    targetLang: string
  ) => void;
  clearCurrentSession: () => void;
}

export function useTranslator(): UseTranslatorReturn {
  const [appMode, setAppMode] = useState<AppMode>("traductor");
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [srcLang, setSrcLang] = useState("auto");
  const [tgtLang, setTgtLang] = useState("es");

  const { settings, saveSettings } = useSettings();
  const { speakTranslation } = useTextToSpeech();
  const { translateText } = useTranslation();
  const { pipActive, updatePip, togglePip } = usePiP();
  
  const {
    meetingTranscript,
    setMeetingTranscript,
    currentMeetingId,
    setCurrentMeetingId,
    subtitleOrig,
    setSubtitleOrig,
    subtitleTran,
    setSubtitleTran,
    addHistory,
    loadPastMeeting,
  } = useMeetings(settings.translatorServiceUrl, false); // we'll pass recording state via effect inside useMeetings

  const { recording, status, statusText, startRecording, stopRecording } = useRecording({
    translatorServiceUrl: settings.translatorServiceUrl,
    srcLang,
    tgtLang,
    voiceEnabled,
    appMode,
    onAddHistory: addHistory,
    onUpdateSubtitle: (orig, tran) => {
      setSubtitleOrig(orig);
      setSubtitleTran(tran);
    },
    onTranslateText: translateText,
    onSpeakTranslation: speakTranslation,
    onUpdatePip: updatePip,
    setCurrentMeetingId,
    setMeetingTranscript,
  });

  const { formattedTime: recordingDuration, resetTimer } = useRecordingTimer(recording);

  const clearCurrentSession = useCallback(() => {
    setMeetingTranscript([]);
    setSubtitleOrig("");
    setSubtitleTran("");
    setCurrentMeetingId(null);
    resetTimer();
  }, [setMeetingTranscript, setSubtitleOrig, setSubtitleTran, setCurrentMeetingId, resetTimer]);

  const toggleRecording = useCallback((onStartedCb?: () => void) => {
    if (recording) {
      stopRecording();
    } else {
      clearCurrentSession();
      startRecording(onStartedCb);
    }
  }, [recording, stopRecording, startRecording, clearCurrentSession]);

  const selectHistoryItem = useCallback((item: TranscriptItem) => {
    setSubtitleOrig(item.orig);
    setSubtitleTran(item.tran);
  }, [setSubtitleOrig, setSubtitleTran]);

  const getStatusColorClass = useCallback(() => {
    switch (status) {
      case "connecting": return "border-yellow-500/30 text-yellow-400 bg-yellow-500/5 before:bg-yellow-400";
      case "reconnecting": return "border-orange-500/30 text-orange-400 bg-orange-500/5 before:bg-orange-400 before:animate-ping";
      case "connected": return "border-emerald-500/30 text-emerald-400 bg-emerald-500/5 before:bg-emerald-400 before:shadow-[0_0_6px_#10b981] before:animate-pulse";
      case "error": return "border-rose-500/30 text-rose-400 bg-rose-500/5 before:bg-rose-400";
      default: return "border-white/10 text-white/40 bg-white/5 before:bg-white/30";
    }
  }, [status]);

  const getLanguageLabel = useCallback((code: string) => {
    const labels: Record<string, string> = { auto: "✨ Auto-detectar", en: "English", es: "Español" };
    return labels[code] || code;
  }, []);

  return {
    appMode, recording, recordingDuration, voiceEnabled, pipActive, status, statusText,
    srcLang, tgtLang, meetingTranscript, currentMeetingId,
    subtitleOrig, subtitleTran, settings,
    setAppMode, setVoiceEnabled, setSrcLang, setTgtLang,
    toggleRecording, togglePip, saveSettings,
    selectHistoryItem,
    speakTranslation: (text, langCode, force) => speakTranslation(text, langCode, force, voiceEnabled),
    getStatusColorClass, getLanguageLabel,
    loadPastMeeting: (id, trans, sl, tl) => loadPastMeeting(id, trans, sl, tl, setSrcLang, setTgtLang),
    clearCurrentSession,
  };
}
